<template>

  <!-- Snackbar -->
  <div
    v-if="showSnackbarFlag"
    :class="['snackbar', snackbarError ? 'snackbar-error' : 'snackbar-success']"
  >
    {{ snackbarMessage }}
  </div>

  <div class="edit-event-page">

    <DefaultHeader
      title="Редактирование ивента"
      class="fixed-header"
      :offset="560"
    />

    <div class="edit-event-content">
      <div class="edit-event-container" v-if="!loadingEvent">

        <!-- Название -->
        <div class="form-group">
          <label>Название</label>
          <input v-model="form.title" type="text" />
        </div>

        <!-- Описание -->
        <div class="form-group">
          <label>Описание</label>
          <textarea v-model="form.description" rows="6" />
        </div>

        <!-- Дата -->
        <div class="form-group">
          <label>Дата проведения</label>
          <input v-model="form.dateOfEvent" type="date" />
        </div>

        <!-- Ссылка -->
        <div class="form-group">
          <label>Ссылка</label>
          <input v-model="form.linkURL" type="text" />
        </div>

        <!-- Город -->
        <div class="form-group">
          <CityAutocompleteForEvent v-model="selectedCity" />
        </div>

        <!-- Компания -->
        <div class="form-group">
          <CompanyAutocompleteForEvent v-model="selectedCompany" />
        </div>

        <!-- Upload -->
        <div class="image-upload-section">
          <label class="drop-area" @click="triggerFileInput">
            <input
              type="file"
              accept="image/*"
              ref="fileInput"
              class="hidden-input"
              @change="handleFileChange"
            />

            <div v-if="uploading">Загрузка...</div>

            <div v-else-if="!imageUrl">
              <img src="/upload-icon.png" class="upload-icon" />
              <p>Добавьте изображение</p>
            </div>

            <div v-else class="image-preview">
              <img :src="imageUrl" />
              <button class="remove-button" @click.stop="removeImage">
                Удалить
              </button>
            </div>
          </label>
        </div>

        <button
          class="event-button"
          :disabled="!isValid"
          @click="submitEvent"
        >
          Сохранить изменения
        </button>

      </div>

      <div v-else class="loader-wrapper">
        <div class="spinner"></div>
      </div>

    </div>

    <Footer />

  </div>

</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from "vue"
import { useRoute, useRouter } from "vue-router"

import DefaultHeader from "../components/DefaultHeader.vue"
import Footer from "../components/MiniFooter.vue"
import CityAutocompleteForEvent from "../components/CityAutocompleteForEvent.vue"
import CompanyAutocompleteForEvent from "../components/profile/CompanyAutocompleteForEvent.vue"

import EventService from "../api/EventService.js"

const route = useRoute()
const router = useRouter()

const eventId = route.params.id

/* ========================= */
/* STATE */
/* ========================= */

const loadingEvent = ref(true)
const uploading = ref(false)
const loadingSubmit = ref(false)

const selectedCompany = ref(null)
const selectedCity = ref(null)
const imageUrl = ref("")

const snackbarMessage = ref("")
const snackbarError = ref(false)
const showSnackbarFlag = ref(false)

function showSnackbar(message, isError = false) {
  snackbarMessage.value = message
  snackbarError.value = isError
  showSnackbarFlag.value = true
  setTimeout(() => (showSnackbarFlag.value = false), 3000)
}

/* ========================= */
/* FORM */
/* ========================= */

const form = reactive({
  title: "",
  description: "",
  dateOfEvent: "",
  imageURL: "",
  linkURL: "",
  cityID: "",
  companyID: ""
})

/* Оригинальные данные */
const originalData = ref(null)

/* ========================= */
/* DIRTY CHECK */
/* ========================= */

const isDirty = computed(() => {
  if (!originalData.value) return false

  return (
    form.title !== originalData.value.title ||
    form.description !== originalData.value.description ||
    form.dateOfEvent !== originalData.value.dateOfEvent ||
    form.imageURL !== originalData.value.imageURL ||
    form.linkURL !== originalData.value.linkURL ||
    form.cityID !== originalData.value.cityID ||
    form.companyID !== originalData.value.companyID
  )
})

/* ========================= */
/* VALID CHECK */
/* ========================= */

const isValid = computed(() => {
  return (
    form.title &&
    form.description &&
    form.dateOfEvent &&
    form.imageURL &&
    form.linkURL &&
    form.cityID &&
    form.companyID &&
    isDirty.value
  )
})

watch(selectedCity, val => {
  form.cityID = val?._id || ""
})

watch(selectedCompany, val => {
  form.companyID = val?.company_id || ""
})

onMounted(async () => {
  try {
    const event = await EventService.fetchEventById(eventId)

    form.title = event.title
    form.description = event.description
    form.linkURL = event.linkURL
    form.imageURL = event.imageURL
    form.cityID = event.cityID
    form.companyID = event.companyID

    imageUrl.value = event.imageURL

    const date = new Date(event.dateOfEventTimestamp)
    form.dateOfEvent = date.toISOString().split("T")[0]

    /* сохраняем оригинал */
    originalData.value = {
      title: form.title,
      description: form.description,
      dateOfEvent: form.dateOfEvent,
      imageURL: form.imageURL,
      linkURL: form.linkURL,
      cityID: form.cityID,
      companyID: form.companyID
    }

  } catch (e) {
    showSnackbar("Ошибка загрузки ивента", true)
  } finally {
    loadingEvent.value = false
  }
})

const fileInput = ref(null)

const triggerFileInput = () => fileInput.value?.click()

const handleFileChange = e => {
  const file = e.target.files[0]
  if (file) handleFileUpload(file)
}

const handleFileUpload = async file => {
  try {
    uploading.value = true
    const uploadedURL = await EventService.uploadEventImage(file, eventId)
    imageUrl.value = uploadedURL
    form.imageURL = uploadedURL
  } finally {
    uploading.value = false
  }
}

const removeImage = () => {
  imageUrl.value = ""
  form.imageURL = ""
}

/* ========================= */
/* SUBMIT */
/* ========================= */

async function submitEvent() {
  try {
    loadingSubmit.value = true

    const payload = {
      title: form.title,
      previewTitle: form.title,
      description: form.description,
      dateOfEvent: new Date(form.dateOfEvent).getTime(),
      imageURL: form.imageURL,
      linkURL: form.linkURL,
      companyID: form.companyID,
      cityID: form.cityID,
      eventID: eventId
    }

    await EventService.createOrUpdateEvent(payload)

    showSnackbar("Ивент обновлён 🎉")

    setTimeout(() => {
      router.push(`/event/${eventId}`)
    }, 1000)

  } catch (e) {
    showSnackbar("Ошибка обновления 😢", true)
  } finally {
    loadingSubmit.value = false
  }
}
</script>
  
<style scoped>
  
.edit-event-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.edit-event-content {
  flex: 1;
  overflow-y: auto;
  padding-top: 100px;   
  padding-bottom: 100px; 
}

/* Центральный контейнер */
.edit-event-container {
  width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 400;
}

.form-group input,
.form-group textarea {
  width: 600px;
  padding: 12px;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  border-radius: 10px;
  border: 1px solid #ccc;
  background: rgb(247, 247, 247);
  transition: 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #2173E5;
  box-shadow: 0 0 0 2px rgba(33, 115, 229, 0.15);
}

[data-theme="dark"] .form-group input,
[data-theme="dark"] .form-group textarea {
  background: #2a2a2a;
  border-color: #444;
  color: white;
}

.image-upload-section {
  margin-top: 20px;
}

.drop-area {
  width: 620px;
  height: 280px;
  border-radius: 16px;
  border: 2px dashed #cfcfcf;
  background: #fafafa;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: 0.2s ease;
  margin-left: -120px;
}

.drop-area:hover {
  border-color: #2173E5;
}

[data-theme="dark"] .drop-area {
  background: #1f1f1f;
  border-color: #444;
}

.hidden-input {
  display: none;
}

.image-preview img {
  width: 100%;
  height: 260px;
  object-fit: cover;
  border-radius: 12px;
}

.remove-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 12px;
  cursor: pointer;
}

.event-button {
  margin-top: 20px;
  width: 630px;
  padding: 14px 32px;
  border-radius: 14px;
  border: none;
  font-size: 15px;
  font-family: "Eastman-Medium", sans-serif;
  color: white;
  background: linear-gradient(to right, #2173E5, #204F90);
  cursor: pointer;
  transition: 0.25s ease;
}

.event-button:hover:not(:disabled) {
  transform: translateY(-2px);
  opacity: 0.9;
}

.event-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ============================= */
/* Loader */
/* ============================= */

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

.spinner {
  border: 6px solid #eee;
  border-top: 6px solid #2173E5;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.snackbar {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-family: "Eastman-Medium";
  color: white;
  z-index: 9999;
  box-shadow: 0 4px 20px rgba(0,0,0,0.25);
}

.snackbar-success {
  background: #3dbf5a;
}

.snackbar-error {
  background: #e74c3c;
}
  
  </style>